import { useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Company } from "models/Company";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useGetContactsQuery } from "app/api/companies";
import Add from "@mui/icons-material/Add";
import LoadingDisplay from "components/LoadingDisplay";
import ContactForm from "../components/ContactForm";
import EmptyMessage from "components/EmptyMessage";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type ContactsSectionProps = { company: Company };

const ContactsSection: React.FC<ContactsSectionProps> = ({ company }) => {
  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  const [editContactId, setEditContactId] = useState<string | null>(null);

  const { data: contacts, isLoading: isLoadingContacts } = useGetContactsQuery({
    companyId: company.id,
  });

  return (
    <>
      <DialogContent sx={{ p: 1 }}>
        {isLoadingContacts ? (
          <LoadingDisplay message="Hämtar kontakter..." />
        ) : (
          <List disablePadding>
            {!contacts?.length && (
              <ListItem divider>
                <ListItemText primary={<EmptyMessage>Inga kontakter hittades</EmptyMessage>} />
              </ListItem>
            )}
            {contacts?.map((contact) =>
              contact.id === editContactId ? (
                <ContactForm
                  key={contact.id}
                  contactToEdit={contact}
                  companyId={company.id}
                  onClose={() => setEditContactId(null)}
                />
              ) : (
                <ListItem
                  key={contact.id}
                  divider
                  secondaryAction={
                    <IconButton
                      hidden={!canEditCompanies}
                      onClick={() => {
                        setShowAddContactDialog(false);
                        setEditContactId(contact.id);
                      }}
                    >
                      <EditOutlined />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={contact.name}
                    secondary={
                      <>
                        <Typography>{contact.phone}</Typography>
                        <Typography>{contact.email}</Typography>
                      </>
                    }
                    slotProps={{ primary: { fontWeight: "bold" } }}
                  />
                </ListItem>
              )
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions hidden={!canEditCompanies} sx={{ p: 1 }}>
        {showAddContactDialog ? (
          <ContactForm companyId={company.id} onClose={() => setShowAddContactDialog(false)} />
        ) : (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditContactId(null);
              setShowAddContactDialog(true);
            }}
            sx={{ ml: "auto" }}
            disabled={isLoadingContacts}
          >
            Ny kontakt
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default ContactsSection;
